import { isAbsolute, join } from "node:path";
import { isDeepStrictEqual } from "node:util";
import type { CodexCredentialDelivery, TokenUsage } from "@skaile/workspaces/types";
import { CodexAppServerClient, type CodexServerRequestHandlers } from "../codex-app-server.js";
import {
  AgentDriver,
  type AgentConfig,
  type AgentEvent,
  type AgentMessage,
  type AgentPromptInput,
  type CodexManagedRuntimeOptions,
  type CodexNativeMcpDiagnostics,
  type CodexToolTransport,
} from "../types.js";
import { parseCodexCredential, parseCodexRefreshResult } from "./credentials.js";
import { codexInstanceEnvironment, prepareCodexInstance } from "./instance-state.js";
import { codexPromptInput } from "./inputs.js";
import {
  prepareNativePolicy,
  activateNativeApprovalPolicy,
  isNativeApprovalPolicyActive,
  type NativeApprovalPolicy,
} from "./native-policy.js";
import {
  createCodexNativeApprovalHandlers,
  type NativeApprovalFileChanges,
} from "./native-approvals.js";
import { createCodexMcpApprovalHandler } from "./native-mcp-approval.js";
import { CodexNativeStateUnavailable } from "./native-state.js";
import { CodexQuestions } from "./questions.js";
import type { NativeNotifications } from "./native-protocol.generated.js";

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const failure = (code = "CODEX_RUNTIME_UNSUPPORTED") => new Error(code);
const nativeUsage = (value: Record<string, unknown>): TokenUsage => ({
  ...(typeof value.inputTokens === "number" ? { inputTokens: value.inputTokens } : {}),
  ...(typeof value.outputTokens === "number" ? { outputTokens: value.outputTokens } : {}),
  ...(typeof value.cachedInputTokens === "number"
    ? { cacheReadTokens: value.cachedInputTokens }
    : {}),
  ...(typeof value.reasoningOutputTokens === "number"
    ? { reasoningTokens: value.reasoningOutputTokens }
    : {}),
  ...(typeof value.totalTokens === "number" ? { totalTokens: value.totalTokens } : {}),
});
const subtractUsage = (total: TokenUsage, baseline?: TokenUsage): TokenUsage => {
  const delta = (value?: number, before?: number) =>
    value === undefined ? undefined : Math.max(0, value - (before ?? 0));
  return {
    ...(delta(total.inputTokens, baseline?.inputTokens) !== undefined
      ? { inputTokens: delta(total.inputTokens, baseline?.inputTokens) }
      : {}),
    ...(delta(total.outputTokens, baseline?.outputTokens) !== undefined
      ? { outputTokens: delta(total.outputTokens, baseline?.outputTokens) }
      : {}),
    ...(delta(total.cacheReadTokens, baseline?.cacheReadTokens) !== undefined
      ? {
          cacheReadTokens: delta(total.cacheReadTokens, baseline?.cacheReadTokens),
        }
      : {}),
    ...(delta(total.reasoningTokens, baseline?.reasoningTokens) !== undefined
      ? {
          reasoningTokens: delta(total.reasoningTokens, baseline?.reasoningTokens),
        }
      : {}),
    ...(delta(total.totalTokens, baseline?.totalTokens) !== undefined
      ? { totalTokens: delta(total.totalTokens, baseline?.totalTokens) }
      : {}),
  };
};
const addUsage = (left: TokenUsage | undefined, right: TokenUsage): TokenUsage => {
  const result: TokenUsage = {};
  for (const key of [
    "inputTokens",
    "outputTokens",
    "cacheReadTokens",
    "reasoningTokens",
    "totalTokens",
  ] as const) {
    if (left?.[key] !== undefined || right[key] !== undefined)
      result[key] = (left?.[key] ?? 0) + (right[key] ?? 0);
  }
  return result;
};
const limit = (value: number | undefined, fallback: number) => {
  const result = value ?? fallback;
  if (!Number.isSafeInteger(result) || result <= 0 || result > 1_800_000)
    throw failure("AI_PROFILE_INVALID");
  return result;
};
async function bounded<T>(work: Promise<T>, signal: AbortSignal, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        cancel = () => reject(failure());
        if (signal.aborted) cancel();
        else signal.addEventListener("abort", cancel, { once: true });
        timer = setTimeout(() => reject(failure()), ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
  }
}
type ActiveTurn = {
  id?: string;
  controller: AbortController;
  done: Promise<void>;
  ready: Promise<void>;
  readyNow: () => void;
  finish: () => void;
  timer: ReturnType<typeof setTimeout>;
  events: Array<{ method: string; params: Record<string, unknown> }>;
  messages: Map<string, string>;
  fileChanges: Map<string, string>;
  toolResults: AgentMessage[];
  toolItems: Set<string>;
  completedToolItems: Set<string>;
  failureCode?: string;
  refreshUsed: boolean;
  usage?: TokenUsage;
  usageBaseline?: TokenUsage;
  usageCursor?: TokenUsage;
  contextTokens?: number;
  contextWindow?: number;
  actualModel: string;
};

/** Internal managed implementation behind the existing CodexDriver registry identity. */
export class ManagedCodexDriver extends AgentDriver {
  readonly driverInfo = {
    id: "codex",
    name: "OpenAI Codex",
    modelAgnostic: false,
    supportsInBandAbort: true,
  };
  private client?: CodexAppServerClient;
  private connecting?: Promise<CodexAppServerClient>;
  private options?: CodexManagedRuntimeOptions;
  private credential?: CodexCredentialDelivery;
  private nativeApprovalPolicy?: NativeApprovalPolicy;
  private instance?: ReturnType<typeof prepareCodexInstance>;
  private tools?: CodexToolTransport;
  private mcpStartup: CodexNativeMcpDiagnostics["startupAtEnd"] = "unknown";
  private pendingMcpStartup?: {
    threadId: string;
    status: CodexNativeMcpDiagnostics["startupAtEnd"];
  };
  private mcpDiagnostics: CodexNativeMcpDiagnostics = {
    startupBeforeTurn: "unknown",
    startupAtEnd: "unknown",
    toolsListBeforeTurn: 0,
    toolsListTotal: 0,
    toolsCallTotal: 0,
    nativeMcpToolItemCount: 0,
  };
  private controller = new AbortController();
  private generation = 0;
  private killed = false;
  private broken = false;
  private cleanupUncertain = false;
  private unexpectedClose = false;
  private starting?: Promise<void>;
  private closing: Promise<void> = Promise.resolve();
  private threadId?: string;
  private lastThreadId?: string;
  private resetting?: Promise<void>;
  private pendingPatch?: Partial<Pick<AgentConfig, "model" | "thinking" | "effort">>;
  private active?: ActiveTurn;
  private threadUsageTotal?: TokenUsage;
  private resumeUsageTurnIds = new Set<string>();
  private queue: Promise<void> = Promise.resolve();
  private queued = 0;
  private admittedTurns = 0;
  private loginCompleted?: {
    resolve: () => void;
    reject: (error: Error) => void;
  };
  private startupRefreshUsed = false;
  private startupAuthAttempted = false;
  private readonly questions = new CodexQuestions((event) => this.event(event));

  override get acceptsReplyAsPrompt(): boolean {
    return false;
  }
  override hasPendingQuestion(): boolean {
    return this.questions.active;
  }
  override answerQuestion(answer: string, question?: string, requestId?: string): boolean {
    return this.questions.answer(answer, question, requestId);
  }

  constructor(private config: AgentConfig) {
    super();
  }
  override get runtimeSessionId(): string | undefined {
    return this.threadId ?? this.lastThreadId;
  }
  get isRunning(): boolean {
    return Boolean(this.client && this.threadId && !this.killed && !this.broken);
  }
  override getModel(): string | undefined {
    return this.config.model;
  }

  async start(): Promise<void> {
    if (this.killed || this.broken) throw failure();
    if (this.threadId && this.client) return;
    if (this.starting) return this.starting;
    this.starting = this.initialize()
      .catch(async (error) => {
        this.broken = true;
        await this.closeGeneration();
        if (
          error instanceof CodexNativeStateUnavailable &&
          (this.cleanupUncertain ||
            this.unexpectedClose ||
            this.killed ||
            this.startupAuthAttempted)
        )
          throw failure();
        const safe =
          error instanceof Error && /^(AI_PROFILE_INVALID|CODEX_[A-Z_]+)$/.test(error.message)
            ? error
            : failure();
        throw safe;
      })
      .finally(() => {
        this.starting = undefined;
      });
    return this.starting;
  }

  private async initialize(): Promise<void> {
    await this.closing;
    if (this.killed) throw failure();
    const options = this.config.driverOptions?.codex?.managedRuntime;
    const tuning = this.config.driverOptions?.codex;
    if (
      tuning?.authSource !== "managed" ||
      !options ||
      typeof options.prepareTools !== "function" ||
      !isAbsolute(options.executable) ||
      !this.config.model ||
      !tuning.reasoningEffort ||
      !tuning.approvalPolicy ||
      tuning.approvalPolicy === "on-failure" ||
      !tuning.sandboxMode ||
      tuning.sandboxMode === "danger-full-access" ||
      (this.config.provider && this.config.provider !== "openai") ||
      this.config.apiKeys?.openai ||
      this.config.env?.OPENAI_API_KEY ||
      (options.nativeTurnBudget !== undefined && options.nativeTurnBudget !== 1)
    )
      throw failure("AI_PROFILE_INVALID");
    this.options = options;
    const timeout = limit(options.requestTimeoutMs, 15_000);
    limit(options.turnTimeoutMs, 300_000);
    try {
      this.credential = parseCodexCredential(this.credential ?? options.credential);
    } catch {
      throw failure("CODEX_AUTH_UNAVAILABLE");
    }
    if (this.credential.configId !== this.config.aiProviderConfigId)
      throw failure("AI_PROFILE_INVALID");
    const privatePolicy = prepareNativePolicy({
      cwd: this.config.cwd,
      privateRoot: options.privateRoot,
      privateDirectory: options.privateDirectory,
      sandboxMode: tuning.sandboxMode,
    });
    this.instance = prepareCodexInstance(this.config, options, this.credential);
    const generation = ++this.generation;
    const mcpReplies = new WeakMap<object, () => boolean>();
    this.controller = new AbortController();
    this.startupRefreshUsed = false;
    this.startupAuthAttempted = false;
    const handlers: CodexServerRequestHandlers = {
      "account/chatgptAuthTokens/refresh": async (params, signal) => {
        const active = this.active;
        if (!active) this.startupAuthAttempted = true;
        const cancellation = AbortSignal.any([
          signal,
          this.controller.signal,
          ...(active ? [active.controller.signal] : []),
        ]);
        try {
          return await this.refresh(params, cancellation);
        } catch {
          if (active && this.active === active) active.failureCode = "CODEX_AUTH_UNAVAILABLE";
          throw failure("CODEX_AUTH_UNAVAILABLE");
        }
      },
    };
    const authMode = this.credential.kind === "codex-subscription" ? "chatgpt" : "api";
    const nativePolicy = {
      cli_auth_credentials_store: "ephemeral",
      forced_login_method: authMode,
      model_provider: "openai",
      model: this.config.model,
      model_instructions_file: this.instance.instructionFile,
      shell_environment_policy: {
        inherit: "none",
        set: { PATH: options.environment.PATH ?? "/usr/bin:/bin" },
      },
      mcp_servers: {},
    };
    const overrides = Object.entries({
      cli_auth_credentials_store: "ephemeral",
      forced_login_method: authMode,
      model_provider: "openai",
      model: this.config.model,
      model_instructions_file: this.instance.instructionFile,
      "shell_environment_policy.inherit": "none",
      "shell_environment_policy.set.PATH": options.environment.PATH ?? "/usr/bin:/bin",
    }).flatMap(([key, value]) => ["-c", `${key}=${JSON.stringify(value)}`]);
    overrides.push("-c", "mcp_servers={}");
    const connecting = CodexAppServerClient.connect(
      {
        command: options.executable,
        args: [
          ...(options.executableArgs ?? []),
          ...overrides,
          ...privatePolicy.cliArgs,
          "app-server",
          "--stdio",
        ],
        cwd: this.instance.root,
        env: codexInstanceEnvironment(options.environment, this.instance.home),
        clientInfo: { name: "skaile-managed-codex", version: "1" },
        processGroup: true,
        requestTimeoutMs: timeout,
        serverRequestTimeoutMs: Math.min(limit(options.interactionTimeoutMs, 300_000), 300_000),
        onServerRequest: handlers,
        canReplyToServerRequest: (method, params) => {
          if (method !== "mcpServer/elicitation/request") return true;
          return record(params) && (mcpReplies.get(params)?.() ?? false);
        },
        onNotification: (method, params) => {
          if (generation === this.generation) this.notification(method, params);
        },
        onClose: () => {
          if (generation !== this.generation) return;
          this.unexpectedClose = true;
          this.broken = true;
          this.loginCompleted?.reject(failure());
          this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
          void this.closeGeneration();
        },
      },
      this.controller.signal,
    );
    this.connecting = connecting;
    try {
      this.client = await connecting;
    } finally {
      if (this.connecting === connecting) this.connecting = undefined;
    }
    if (generation !== this.generation || this.killed) throw failure();
    await this.authenticate(timeout);
    const toolsPromise = options.prepareTools(this.controller.signal).then(async (tools) => {
      if (generation !== this.generation || this.controller.signal.aborted) {
        await tools.dispose().catch(() => {});
        throw failure();
      }
      return tools;
    });
    try {
      this.tools = await bounded(toolsPromise, this.controller.signal, timeout);
    } catch {
      throw failure("CODEX_REQUIRED_TOOL_UNAVAILABLE");
    }
    if (
      !this.tools ||
      !record(this.tools.mcpServers) ||
      typeof this.tools.dispose !== "function" ||
      (Object.keys(this.tools.mcpServers).length === 0 &&
        Object.keys(this.tools.onServerRequest ?? {}).length === 0)
    )
      throw failure("CODEX_REQUIRED_TOOL_UNAVAILABLE");
    if (this.tools.onServerRequest?.["item/tool/requestUserInput"])
      throw failure("AI_PROFILE_INVALID");
    const nativeApprovals = createCodexNativeApprovalHandlers({
      questions: this.questions,
      getPolicy: () => (tuning.approvalPolicy === "never" ? undefined : this.nativeApprovalPolicy),
      getFileChanges: ({ itemId, threadId, turnId }) => {
        const active = this.active;
        if (
          !active ||
          active.controller.signal.aborted ||
          threadId !== this.threadId ||
          turnId !== active.id
        )
          return undefined;
        const snapshot = active.fileChanges.get(itemId);
        return snapshot ? (JSON.parse(snapshot) as NativeApprovalFileChanges) : undefined;
      },
    });
    const approvalTools = this.tools;
    const approvalServerName = approvalTools.approvalServerName;
    const approvalServer = approvalServerName
      ? approvalTools.mcpServers[approvalServerName]
      : undefined;
    const approvalServerSnapshot = structuredClone(approvalServer);
    const mcpBindingActive = () =>
      this.tools === approvalTools &&
      approvalTools.approvalServerName === approvalServerName &&
      Boolean(approvalServerName) &&
      Object.hasOwn(approvalTools.mcpServers, approvalServerName!) &&
      record(approvalServer) &&
      approvalTools.mcpServers[approvalServerName!] === approvalServer &&
      isDeepStrictEqual(approvalServer, approvalServerSnapshot);
    const mcpPolicy = () =>
      tuning.approvalPolicy === "never" ? undefined : this.nativeApprovalPolicy;
    const mcpApproval = createCodexMcpApprovalHandler({
      serverName: approvalServerName,
      questions: this.questions,
      getPolicy: mcpPolicy,
      bindingActive: mcpBindingActive,
    });
    for (const [method, handler] of Object.entries({
      ...this.tools.onServerRequest,
      ...nativeApprovals,
      "item/tool/requestUserInput": this.questions.request,
      "mcpServer/elicitation/request": mcpApproval,
    })) {
      if (method === "account/chatgptAuthTokens/refresh" || !handler)
        throw failure("AI_PROFILE_INVALID");
      (handlers as Record<string, (params: unknown, signal: AbortSignal) => Promise<unknown>>)[
        method
      ] = async (params, signal) => {
        const active = this.active;
        if (generation !== this.generation || !active || !record(params)) throw failure();
        await active.ready;
        if (
          generation !== this.generation ||
          this.active !== active ||
          params.threadId !== this.threadId ||
          params.turnId !== active.id
        )
          throw failure();
        const cancellation = AbortSignal.any([signal, active.controller.signal]);
        if (cancellation.aborted) throw failure();
        if (method === "mcpServer/elicitation/request") {
          mcpReplies.set(
            params,
            () =>
              generation === this.generation &&
              this.active === active &&
              !cancellation.aborted &&
              params.threadId === this.threadId &&
              params.turnId === active.id &&
              mcpBindingActive() &&
              isNativeApprovalPolicyActive(mcpPolicy(), this.threadId ?? ""),
          );
        }
        const result = await (
          handler as (params: unknown, signal: AbortSignal) => Promise<unknown>
        )(params, cancellation);
        if (generation !== this.generation || cancellation.aborted || this.active !== active)
          throw failure();
        return result;
      };
    }
    if (this.tools.instructions) this.instance.setToolInstructions(this.tools.instructions);
    const threadParams = {
      model: this.config.model,
      modelProvider: "openai",
      cwd: this.instance.cwd,
      approvalPolicy: tuning.approvalPolicy,
      permissions: "managed",
      config: {
        ...nativePolicy,
        ...privatePolicy.config,
        mcp_servers: this.tools.mcpServers,
        model_reasoning_effort: this.config.effort ?? tuning.reasoningEffort,
      },
    };
    const resuming = Boolean(this.config.resumeSessionId);
    this.threadUsageTotal = resuming ? (this.threadUsageTotal ?? {}) : {};
    this.resumeUsageTurnIds.clear();
    const response = resuming
      ? await this.client.request("thread/resume", {
          ...threadParams,
          threadId: this.config.resumeSessionId!,
          excludeTurns: false,
        })
      : await this.client.request("thread/start", {
          ...threadParams,
          allowProviderModelFallback: false,
        });
    if (!record(response) || response.model !== this.config.model)
      throw failure("CODEX_MODEL_UNAVAILABLE");
    if (
      !record(response.thread) ||
      typeof response.thread.id !== "string" ||
      (resuming && response.thread.id !== this.config.resumeSessionId) ||
      (resuming && !Array.isArray(response.thread.turns)) ||
      response.reasoningEffort !== (this.config.effort ?? tuning.reasoningEffort) ||
      response.modelProvider !== "openai" ||
      response.approvalPolicy !== tuning.approvalPolicy
    )
      throw failure("AI_PROFILE_INVALID");
    if (generation !== this.generation || this.killed) throw failure();
    this.resumeUsageTurnIds = new Set(
      resuming && Array.isArray(response.thread.turns)
        ? response.thread.turns.flatMap((turn) =>
            record(turn) && typeof turn.id === "string" ? [turn.id] : [],
          )
        : [],
    );
    this.nativeApprovalPolicy = activateNativeApprovalPolicy(
      privatePolicy,
      response,
      this.controller.signal,
    );
    this.threadId = response.thread.id;
    this.mcpStartup =
      this.pendingMcpStartup?.threadId === this.threadId
        ? this.pendingMcpStartup.status
        : "unknown";
    this.pendingMcpStartup = undefined;
    this.lastThreadId = this.threadId;
    this.instance.bindThread(this.threadId);
  }

  private async authenticate(timeout: number): Promise<void> {
    const credential = this.credential!;
    if (credential.kind === "openai-api-key") {
      const result = await this.client!.request("account/login/start", {
        type: "apiKey",
        apiKey: credential.apiKey,
      });
      if (result.type !== "apiKey") throw failure("CODEX_AUTH_UNAVAILABLE");
      return;
    }
    const completion = new Promise<void>((resolve, reject) => {
      this.loginCompleted = { resolve, reject };
    });
    // A native completion can precede its RPC reply; install the waiter first.
    const wait = bounded(completion, this.controller.signal, timeout);
    void wait.catch(() => {});
    try {
      const result = await this.client!.request("account/login/start", {
        type: "chatgptAuthTokens",
        accessToken: credential.accessToken,
        chatgptAccountId: credential.chatgptAccountId,
        chatgptPlanType: credential.chatgptPlanType ?? null,
      });
      if (result.type !== "chatgptAuthTokens") throw failure("CODEX_AUTH_UNAVAILABLE");
      await wait;
    } finally {
      this.loginCompleted = undefined;
    }
  }

  private async refresh(params: unknown, signal: AbortSignal) {
    const credential = this.credential;
    if (
      !record(params) ||
      params.reason !== "unauthorized" ||
      !credential ||
      credential.kind !== "codex-subscription" ||
      (params.previousAccountId != null &&
        params.previousAccountId !== credential.chatgptAccountId) ||
      !this.options?.refreshCredential ||
      (this.active ? this.active.refreshUsed : this.startupRefreshUsed)
    )
      throw failure("CODEX_AUTH_UNAVAILABLE");
    if (this.active) this.active.refreshUsed = true;
    else this.startupRefreshUsed = true;
    const result = await this.options.refreshCredential(
      {
        kind: "codex-subscription",
        configId: credential.configId,
        credentialVersion: credential.credentialVersion,
        reason: "retry-401",
      },
      signal,
    );
    if (signal.aborted) throw failure("CODEX_AUTH_UNAVAILABLE");
    let parsed: ReturnType<typeof parseCodexRefreshResult>;
    try {
      parsed = parseCodexRefreshResult(result, credential);
    } catch {
      throw failure("CODEX_AUTH_UNAVAILABLE");
    }
    if (!parsed.ok) throw failure("CODEX_AUTH_UNAVAILABLE");
    this.credential = parsed.credential;
    return {
      accessToken: parsed.credential.accessToken,
      chatgptAccountId: parsed.credential.chatgptAccountId,
      chatgptPlanType: parsed.credential.chatgptPlanType ?? null,
    };
  }

  prompt(message: string): Promise<void> {
    return this.promptInput({ text: message });
  }

  override promptInput(input: AgentPromptInput): Promise<void> {
    const message = structuredClone(input);
    const turnBudget = this.config.driverOptions?.codex?.managedRuntime?.nativeTurnBudget;
    if (
      this.killed ||
      this.broken ||
      this.resetting ||
      this.queued >= 32 ||
      (turnBudget !== undefined && this.admittedTurns >= turnBudget)
    )
      return Promise.reject(failure());
    if (turnBudget !== undefined) this.admittedTurns++;
    this.queued++;
    const task = this.queue.then(() => this.runTurn(message));
    this.queue = task
      .catch(() => {})
      .finally(() => {
        this.queued--;
      });
    return task;
  }

  private async runTurn(message: AgentPromptInput): Promise<void> {
    if (this.resetting) throw failure();
    await this.start();
    if (this.killed || this.broken || this.resetting) throw failure();
    if (this.pendingPatch) {
      this.config = { ...this.config, ...this.pendingPatch };
      this.pendingPatch = undefined;
    }
    if (!this.config.model) throw failure("AI_PROFILE_INVALID");
    await this.tools?.validate?.();
    if (this.tools?.needsReload?.()) {
      this.config = { ...this.config, resumeSessionId: this.threadId };
      await this.closeGeneration();
      await this.start();
    }
    const input = codexPromptInput(
      message,
      this.instance!.cwd,
      this.options?.skillsDirectory
        ? {
            source: this.options.skillsDirectory,
            target: join(this.instance!.home, "skills"),
          }
        : undefined,
    );
    try {
      parseCodexCredential(this.credential);
    } catch {
      throw failure("CODEX_AUTH_UNAVAILABLE");
    }
    let finish!: () => void;
    let readyNow!: () => void;
    const ready = new Promise<void>((resolve) => {
      readyNow = resolve;
    });
    const done = new Promise<void>((resolve) => {
      finish = resolve;
    });
    const active: ActiveTurn = {
      controller: new AbortController(),
      done,
      finish,
      ready,
      readyNow,
      timer: setTimeout(
        () => {
          this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
          this.broken = true;
          void this.closeGeneration();
        },
        limit(this.options?.turnTimeoutMs, 300_000),
      ),
      events: [],
      messages: new Map(),
      fileChanges: new Map(),
      toolResults: [],
      toolItems: new Set(),
      completedToolItems: new Set(),
      refreshUsed: false,
      usageBaseline: this.threadUsageTotal,
      actualModel: this.config.model!,
    };
    this.active = active;
    this.mcpDiagnostics.nativeMcpToolItemCount = 0;
    this.observeMcp(true);
    this.tools?.beginTurn?.(active.controller.signal);
    try {
      const response = await this.client!.request("turn/start", {
        threadId: this.threadId!,
        permissions: "managed",
        input,
        model: this.config.model,
        effort: this.config.effort ?? this.config.driverOptions!.codex!.reasoningEffort,
      });
      if (this.active !== active) return;
      if (!record(response.turn) || typeof response.turn.id !== "string") throw failure();
      active.id = response.turn.id;
      active.readyNow();
      for (const event of active.events.splice(0)) this.notification(event.method, event.params);
      if (this.active === active && response.turn.status !== "inProgress")
        this.complete(response.turn);
      await done;
    } catch {
      this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
    }
  }

  async abort(): Promise<void> {
    const active = this.active;
    if (!active) return;
    active.controller.abort();
    try {
      if (!active.id)
        await bounded(
          active.ready,
          this.controller.signal,
          limit(this.options?.requestTimeoutMs, 15_000),
        );
      if (this.active !== active) return;
      await this.client!.request("turn/interrupt", {
        threadId: this.threadId!,
        turnId: active.id!,
      });
      await bounded(
        active.done,
        this.controller.signal,
        limit(this.options?.requestTimeoutMs, 15_000),
      );
    } catch {
      this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
      this.broken = true;
      await this.closeGeneration();
    }
  }

  kill(): void {
    if (this.killed) return;
    this.killed = true;
    this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
    void this.closeGeneration();
  }
  override async dispose(): Promise<void> {
    this.kill();
    await this.starting?.catch(() => {});
    await this.closing;
  }
  async resetSession(): Promise<void> {
    if (this.killed || this.broken) throw failure();
    if (this.resetting) return this.resetting;
    this.resetting = Promise.resolve()
      .then(async () => {
        if (this.active) await this.abort();
        await this.queue;
        await this.starting;
        await this.closeGeneration();
        if (this.killed || this.broken) throw failure();
        this.config = { ...this.config, resumeSessionId: undefined };
        this.lastThreadId = undefined;
        this.threadUsageTotal = undefined;
      })
      .finally(() => {
        this.resetting = undefined;
      });
    return this.resetting;
  }
  override reconfigure(patch: Partial<Pick<AgentConfig, "model" | "thinking" | "effort">>): void {
    this.pendingPatch = { ...this.pendingPatch, ...patch };
  }

  private closeGeneration(): Promise<void> {
    const generation = ++this.generation;
    const finalMcp = { ...this.mcpDiagnostics };
    this.mcpStartup = "unknown";
    this.pendingMcpStartup = undefined;
    this.controller.abort();
    this.nativeApprovalPolicy = undefined;
    this.loginCompleted?.reject(failure());
    this.loginCompleted = undefined;
    const client = this.client;
    this.client = undefined;
    const connecting = this.connecting;
    this.connecting = undefined;
    const tools = this.tools;
    this.tools = undefined;
    const instance = this.instance;
    this.instance = undefined;
    this.threadId = undefined;
    this.closing = this.closing
      .then(async () => {
        try {
          // The handshake owns a spawned child before it returns the client. Join its
          // cancellation/disposal before releasing the instance lease.
          const pendingClient = await connecting?.catch(() => undefined);
          await client?.dispose();
          if (pendingClient && pendingClient !== client) await pendingClient.dispose();
          if (
            (client && !client.terminationConfirmed) ||
            (pendingClient && !pendingClient.terminationConfirmed)
          )
            this.cleanupUncertain = true;
          if (tools) {
            try {
              await bounded(
                Promise.resolve().then(() => tools.dispose()),
                new AbortController().signal,
                1000,
              );
              if (generation === this.generation && !this.cleanupUncertain)
                this.observeClosedMcp(tools, finalMcp);
            } catch {
              this.cleanupUncertain = true;
            }
          }
        } finally {
          instance?.release();
        }
      })
      .catch(() => {
        this.cleanupUncertain = true;
      });
    return this.closing;
  }

  private notification(method: string, params: unknown): void {
    if (!record(params)) return;
    if (method === "mcpServer/startupStatus/updated") {
      const serverName = this.tools?.diagnostics?.serverName;
      if (
        !serverName ||
        params.name !== serverName ||
        typeof params.threadId !== "string" ||
        params.threadId.length > 512 ||
        typeof params.status !== "string" ||
        !["starting", "ready", "failed", "cancelled"].includes(params.status)
      )
        return;
      const status =
        params.status as NativeNotifications["mcpServer/startupStatus/updated"]["status"];
      if (this.threadId) {
        if (params.threadId === this.threadId) this.mcpStartup = status;
      } else this.pendingMcpStartup = { threadId: params.threadId, status };
      return;
    }
    if (method === "account/login/completed") {
      if (params.success === true && params.loginId == null) this.loginCompleted?.resolve();
      else this.loginCompleted?.reject(failure("CODEX_AUTH_UNAVAILABLE"));
      return;
    }
    const active = this.active;
    if (
      (!active || (!active.usageCursor && this.resumeUsageTurnIds.has(String(params.turnId)))) &&
      method === "thread/tokenUsage/updated" &&
      params.threadId === (this.threadId ?? this.config.resumeSessionId) &&
      record(params.tokenUsage) &&
      record(params.tokenUsage.total)
    ) {
      // Resume replay follows its response but precedes new turn events; the
      // caller may already be awaiting turn/start when historical usage arrives.
      this.threadUsageTotal = nativeUsage(params.tokenUsage.total);
      if (active) active.usageBaseline = this.threadUsageTotal;
      return;
    }
    if (!active || params.threadId !== this.threadId) return;
    if (!active.id) {
      if (active.events.length >= 256) {
        this.broken = true;
        this.finishTurn("CODEX_RUNTIME_UNSUPPORTED");
        void this.closeGeneration();
      } else active.events.push({ method, params });
      return;
    }
    const turnId = record(params.turn) ? params.turn.id : params.turnId;
    if (turnId !== active.id) return;
    if (method === "thread/tokenUsage/updated" && record(params.tokenUsage)) {
      const last = params.tokenUsage.last;
      const total = params.tokenUsage.total;
      const cumulativeUsage = record(total) ? nativeUsage(total) : undefined;
      if (cumulativeUsage) {
        // Attach replay precedes live usage; no replay means no historical
        // TokenCount. The first live response therefore counts from zero.
        active.usageBaseline ??= {};
        this.resumeUsageTurnIds.clear();
        const cursor = active.usageCursor ?? active.usageBaseline;
        const reset =
          cursor?.totalTokens !== undefined &&
          cumulativeUsage.totalTokens !== undefined &&
          cumulativeUsage.totalTokens < cursor.totalTokens;
        const increment = reset ? cumulativeUsage : subtractUsage(cumulativeUsage, cursor);
        if (Object.values(increment).some((value) => typeof value === "number" && value > 0))
          active.usage = addUsage(active.usage, increment);
        active.usageCursor = cumulativeUsage;
        this.threadUsageTotal = cumulativeUsage;
      }
      if (record(last)) {
        if (typeof last.totalTokens === "number") active.contextTokens = last.totalTokens;
      }
      if (typeof params.tokenUsage.modelContextWindow === "number")
        active.contextWindow = params.tokenUsage.modelContextWindow;
    } else if (method === "model/rerouted") {
      active.actualModel = typeof params.toModel === "string" ? params.toModel : active.actualModel;
      active.failureCode = "CODEX_MODEL_UNAVAILABLE";
    } else if (method === "turn/completed" && record(params.turn)) this.complete(params.turn);
    else if (method === "error" && params.willRetry !== true)
      active.failureCode = this.nativeErrorCode(params.error);
    else if (
      method === "item/agentMessage/delta" &&
      typeof params.itemId === "string" &&
      typeof params.delta === "string"
    ) {
      const previous = active.messages.get(params.itemId);
      if (previous === undefined)
        this.event({
          type: "message_start",
          message: { role: "assistant", content: [{ type: "text", text: "" }] },
        });
      const text = (previous ?? "") + params.delta;
      active.messages.set(params.itemId, text);
      this.event({
        type: "message_update",
        message: { role: "assistant", content: [{ type: "text", text }] },
        _textDelta: params.delta,
      });
    } else if (
      (method === "item/started" || method === "item/completed") &&
      record(params.item) &&
      typeof params.item.id === "string"
    ) {
      this.item(
        params.item as NativeNotifications["item/started"]["item"],
        method === "item/completed",
      );
    }
  }

  private item(item: NativeNotifications["item/started"]["item"], completed: boolean): void {
    const active = this.active!;
    if (item.type === "fileChange") {
      active.fileChanges.delete(item.id);
      if (
        !completed &&
        Array.isArray(item.changes) &&
        item.changes.length <= 16 &&
        active.fileChanges.size < 16
      ) {
        const snapshot = JSON.stringify(item.changes);
        if (snapshot.length <= 65536) active.fileChanges.set(item.id, snapshot);
      }
    }
    if (item.type === "agentMessage") {
      if (completed && typeof item.text === "string") {
        const previous = active.messages.get(item.id) ?? "";
        if (!active.messages.has(item.id))
          this.event({
            type: "message_start",
            message: {
              role: "assistant",
              content: [{ type: "text", text: "" }],
            },
          });
        if (item.text.startsWith(previous) && item.text.length > previous.length)
          this.event({
            type: "message_update",
            message: {
              role: "assistant",
              content: [{ type: "text", text: item.text }],
            },
            _textDelta: item.text.slice(previous.length),
          });
        active.messages.set(item.id, item.text);
      }
      return;
    }
    const name =
      item.type === "commandExecution"
        ? "Bash"
        : item.type === "fileChange"
          ? "ApplyPatch"
          : item.type === "mcpToolCall"
            ? `mcp__${item.server}__${item.tool}`
            : undefined;
    if (!name) return;
    if (!active.toolItems.has(item.id)) {
      if (item.type === "mcpToolCall" && item.server === this.tools?.diagnostics?.serverName)
        this.mcpDiagnostics.nativeMcpToolItemCount = Math.min(
          512,
          this.mcpDiagnostics.nativeMcpToolItemCount + 1,
        );
      active.toolItems.add(item.id);
      this.event({
        type: "tool_call",
        name,
        tool: { name },
        toolCallId: item.id,
        input:
          item.type === "mcpToolCall"
            ? item.arguments
            : item.type === "commandExecution"
              ? { command: item.command }
              : item.type === "fileChange"
                ? { changes: item.changes }
                : {},
      });
    }
    if (completed && !active.completedToolItems.has(item.id)) {
      active.completedToolItems.add(item.id);
      active.toolResults.push({
        role: "tool",
        toolName: name,
        content: "",
        data:
          item.type === "mcpToolCall"
            ? {
                content: item.result?.content,
                structuredContent: item.result?.structuredContent,
                status: item.status,
              }
            : item.type === "commandExecution"
              ? {
                  output: item.aggregatedOutput,
                  exitCode: item.exitCode,
                  status: item.status,
                }
              : item.type === "fileChange"
                ? { changes: item.changes, status: item.status }
                : {},
        isError: "status" in item && item.status === "failed",
      });
      this.event({ type: "tool_execution_end", toolName: name });
    }
  }
  private complete(turn: Record<string, unknown>): void {
    if (turn.status === "completed") this.finishTurn(this.active?.failureCode);
    else if (turn.status === "interrupted") this.finishTurn("CODEX_TURN_INTERRUPTED");
    else if (turn.status === "failed")
      this.finishTurn(this.active?.failureCode ?? this.nativeErrorCode(turn.error));
  }
  private nativeErrorCode(error: unknown): string {
    const info = record(error) ? error.codexErrorInfo : undefined;
    if (info === "unauthorized") return "CODEX_RECONNECT_REQUIRED";
    if (
      info === "usageLimitExceeded" ||
      info === "rateLimitExceeded" ||
      info === "serverOverloaded"
    )
      return "CODEX_RATE_LIMITED";
    if (
      record(info) &&
      [
        "httpConnectionFailed",
        "responseStreamConnectionFailed",
        "responseStreamDisconnected",
        "responseTooManyFailedAttempts",
      ].some((key) => key in info)
    )
      return "CODEX_NETWORK_UNAVAILABLE";
    return "CODEX_RUNTIME_UNSUPPORTED";
  }

  private observeMcp(beforeTurn: boolean): void {
    const count = (value: unknown) =>
      typeof value === "number" && Number.isSafeInteger(value) && value >= 0
        ? Math.min(512, value)
        : 0;
    let listed = 0;
    let called = 0;
    try {
      const counts = this.tools?.diagnostics?.requestCounts();
      listed = count(counts?.toolsListTotal);
      called = count(counts?.toolsCallTotal);
    } catch {
      /* Observation must not affect execution or cleanup. */
    }
    if (beforeTurn) {
      this.mcpDiagnostics.startupBeforeTurn = this.mcpStartup;
      this.mcpDiagnostics.toolsListBeforeTurn = listed;
    }
    this.mcpDiagnostics.startupAtEnd = this.mcpStartup;
    this.mcpDiagnostics.toolsListTotal = Math.max(this.mcpDiagnostics.toolsListBeforeTurn, listed);
    this.mcpDiagnostics.toolsCallTotal = beforeTurn
      ? called
      : Math.max(this.mcpDiagnostics.toolsCallTotal, called);
    this.publishMcp(this.mcpDiagnostics);
  }

  private observeClosedMcp(tools: CodexToolTransport, snapshot: CodexNativeMcpDiagnostics): void {
    try {
      // No RPC: the owned transport has closed, so its local counters are final.
      const counts = tools.diagnostics?.requestCounts();
      if (
        !counts ||
        ![counts.toolsListTotal, counts.toolsCallTotal].every(
          (value) => Number.isSafeInteger(value) && value >= 0,
        )
      )
        return;
      snapshot.toolsListTotal = Math.max(
        snapshot.toolsListTotal,
        Math.min(512, counts.toolsListTotal),
      );
      snapshot.toolsCallTotal = Math.max(
        snapshot.toolsCallTotal,
        Math.min(512, counts.toolsCallTotal),
      );
      this.publishMcp(snapshot);
    } catch {
      // No final observation on getter failure; retain the earlier snapshot unchanged.
    }
  }

  private publishMcp(snapshot: CodexNativeMcpDiagnostics): void {
    try {
      void Promise.resolve(
        this.options?.onNativeMcpDiagnostics?.(Object.freeze({ ...snapshot })),
      ).catch(() => {});
    } catch {
      /* A caller-owned observer is never an execution gate. */
    }
  }

  private finishTurn(code?: string): void {
    const active = this.active;
    if (!active) return;
    this.active = undefined;
    this.observeMcp(false);
    clearTimeout(active.timer);
    active.controller.abort();
    active.fileChanges.clear();
    active.readyNow();
    for (const text of active.messages.values())
      this.event({
        type: "message_end",
        message: { role: "assistant", content: [{ type: "text", text }] },
      });
    this.event({ type: "turn_end", toolResults: active.toolResults });
    const modelUsage = active.usage
      ? {
          inputTokens: active.usage.inputTokens,
          outputTokens: active.usage.outputTokens,
          cacheReadTokens: active.usage.cacheReadTokens,
          ...(typeof active.contextWindow === "number"
            ? { contextWindow: active.contextWindow }
            : {}),
        }
      : undefined;
    const usage = {
      ...(active.usage ? { tokens: active.usage } : {}),
      ...(modelUsage ? { modelUsage: { [active.actualModel]: modelUsage } } : {}),
      ...(typeof active.contextTokens === "number" ? { contextTokens: active.contextTokens } : {}),
      ...(typeof active.contextWindow === "number" ? { contextWindow: active.contextWindow } : {}),
    };
    if (code)
      this.event({
        type: "error",
        error: code,
        fatal: code !== "CODEX_TURN_INTERRUPTED",
        detail: {
          message: code,
          category:
            code === "CODEX_TURN_INTERRUPTED"
              ? "process"
              : code === "CODEX_RATE_LIMITED"
                ? "rate_limit"
                : code === "CODEX_NETWORK_UNAVAILABLE"
                  ? "network"
                  : code === "CODEX_AUTH_UNAVAILABLE" || code === "CODEX_RECONNECT_REQUIRED"
                    ? "auth"
                    : "config",
          retryable: false,
        },
        ...usage,
      });
    else
      this.event({
        type: "result",
        subtype: "success",
        summary: [...active.messages.values()].join("\n"),
        ...usage,
      });
    this.event({ type: "agent_end" });
    active.finish();
  }
  private event(event: AgentEvent): void {
    // Observers cannot prevent native cleanup or leave a terminal prompt unsettled.
    try {
      this.emit("agent-event", event);
    } catch {}
  }
}
